import { t } from 'i18next';
import { Box3, Intersection, Object3D, Points, Ray, Raycaster, Vector3 } from 'three';
import _ from 'underscore';

import { ProgressController } from '../../../../react/utils/progress-controller';
import { PromiseAbortSignal } from '../../../../react/utils/promise-abort-controller';
import { Cache } from '../../../cache/cache-manager';
import { SkyviewCamera } from '../../../cameras/types';
import { CesiumTileSetHierarchy } from '../../../loader/cesium/tile-set';
import { TileNode } from '../../../loader/cesium/tile-set/types';
import {
  createTileNodeDataUri,
  extractTileNodeBoundingSphere,
} from '../../../loader/cesium/tile-set/utilities';
import { closestToRay } from '../../../utilities/pick-near-cursor';
import { CesiumPntsWebworkerResponse } from '../../../workers/fetch-cesium-pnts';
import { getWebWorkerManager } from '../../../workers/web-worker-manager';
import { PointCloud } from '../types';
import { getAttributePositionAtIndex } from './points/buffer-attribute/position-buffer-attribute';
import { CesiumPoints } from './points/cesium-points';
import { PointsFactory } from './points/types';

/**
 *  Cesium point cloud handler.
 */
export class CesiumPointCloud extends PointCloud {
  private boundingBox: Box3 = new Box3();

  private cache: Cache;

  private lastTick: number = Date.now();

  private tileNodeLoadQueue: Map<string, TileNode> = new Map<string, TileNode>();
  private maxLoadQueueSize = 6;

  constructor(
    private cesiumTileSetHierarchy: CesiumTileSetHierarchy,
    readonly origo: Vector3,
    private pointsBuilder: PointsFactory,
  ) {
    super();
    this.cache = new Cache();
  }

  initialize() {
    getWebWorkerManager().init('cesiumPnts');

    this.generateBoundingBox();
  }

  /**
   *  Filters the chunks based on the camera frustum.
   *  Currently only return the root chunk. Will be implemented later on.
   */
  async loadLodChunks(camera: SkyviewCamera) {
    if (!this.resolveLoadChunks()) {
      return;
    }
    this.lastTick = Date.now();

    // Sorts the nodes to ensure we load the highest node level closest to the camera ray first
    const nodesToLoad = await this.cesiumTileSetHierarchy.filterTileNodesToBeLoaded(
      camera,
      this.cache,
      this.position,
    );

    nodesToLoad.sort((a, b) => {
      if (a.index === b.index) {
        return a.distanceToRay - b.distanceToRay;
      }
      return b.index - a.index;
    });

    // Do not queue any tile nodes if the queue is full
    if (this.tileNodeLoadQueue.size >= this.maxLoadQueueSize) {
      return;
    }

    // Do only add up to the queue limit of chunks to be loaded simultaneously
    const nodesInQueue = this.maxLoadQueueSize - this.tileNodeLoadQueue.size;
    const tileNodesToLoad = nodesToLoad.slice(0, Math.min(nodesToLoad.length, nodesInQueue));

    await this.loadTileNodes(
      tileNodesToLoad.map((node) => {
        this.cache.queue(createTileNodeDataUri(node.node, node.node.content!.uri!));
        return node.node;
      }),
    );
  }

  /**
   * Loads the max detailed chunks that intersects with the given rays.
   */
  async getMaxLodChunksFromRays(
    rays: Ray[],
    signal?: PromiseAbortSignal,
    progressController?: ProgressController,
  ): Promise<Points[]> {
    const allNodes = await this.cesiumTileSetHierarchy.filterMaxLodChunks(
      this.cache,
      this.position,
    );

    if (signal?.aborted) {
      return [];
    }

    const tileNodesToLoad: TileNode[] = [];
    const invertVector = new Vector3(1, 1, -1);
    const targetVector = new Vector3();
    const invertedRays = rays
      .map((x) => x.clone())
      .map((x) => {
        x.direction.multiply(invertVector);
        return x;
      });

    for (const node of allNodes) {
      const sphere = extractTileNodeBoundingSphere(node, this.position);

      for (let i = 0; i < rays.length; i++) {
        if (
          rays[i].intersectSphere(sphere, targetVector) ||
          invertedRays[i].intersectSphere(sphere, targetVector)
        ) {
          tileNodesToLoad.push(node);
          break;
        }
      }
    }

    if (signal?.aborted) {
      return [];
    }

    for (const node of tileNodesToLoad) {
      this.cache.queue(createTileNodeDataUri(node, node.content!.uri!));
    }
    await this.loadTileNodes(tileNodesToLoad, signal, progressController);

    return tileNodesToLoad.map((x) => this.cache.get(createTileNodeDataUri(x, x.content!.uri!)));
  }

  /**
   * Loads the max detailed chunks within the boundin box into memory.
   */
  async getMaxLodChunksWithinBoundingBox(
    bbox: Box3,
    signal?: PromiseAbortSignal,
    progressController?: ProgressController,
  ): Promise<Points[]> {
    const tileNodesToLoad = await this.cesiumTileSetHierarchy.filterMaxLodChunks(
      this.cache,
      this.position,
      bbox,
    );

    if (signal?.aborted) {
      return [];
    }

    for (const node of tileNodesToLoad) {
      this.cache.queue(createTileNodeDataUri(node, node.content!.uri!));
    }
    await this.loadTileNodes(tileNodesToLoad, signal, progressController);

    return tileNodesToLoad.map((x) => this.cache.get(createTileNodeDataUri(x, x.content!.uri!)));
  }

  /**
   *  Attaches the point clouds chunks that are loaded and within the frustum
   */
  cullLodChunks(camera: SkyviewCamera) {
    const newChildren = this.cesiumTileSetHierarchy.resolveTileNodesToBeRendered(
      camera,
      this.cache,
      this.position,
    );

    this.remove(...this.children);
    if (newChildren && newChildren.length > 0) {
      this.add(...newChildren);
    }
  }

  /**
   *  Returns the intersection of the ray to the point cloud closest to the ray, for rays that
   *  aren't from the camera. Rays from the camera pick with pickNearCursor.
   */
  pick(rayCaster: Raycaster, points?: Points[]): Intersection[] {
    const closest = closestToRay(this.intersectChunks(rayCaster, points ?? this.children));
    return closest ? [closest] : [];
  }

  intersect(rayCaster: Raycaster): Intersection[] {
    // The children are the chunks that are shown, the others are culled
    return this.intersectChunks(rayCaster, this.children);
  }

  private intersectChunks(rayCaster: Raycaster, chunks: Object3D[]): Intersection[] {
    const intersections = chunks.flatMap((chunk) =>
      chunk instanceof CesiumPoints ? chunk.raycasterIntersection(rayCaster) : [],
    );

    for (const intersection of intersections) {
      intersection.point = getAttributePositionAtIndex(
        intersection.object as Points,
        intersection.index!,
        intersection.object.position,
      ).add(this.position);
      intersection.distanceToRay = rayCaster.ray.distanceToPoint(intersection.point);
    }
    return intersections;
  }

  available() {
    return true;
  }

  getBoundingBox() {
    return this.boundingBox;
  }

  getOrigo() {
    return this.origo;
  }

  clearCustom() {
    this.cache.clearCache();

    getWebWorkerManager().stopChunks('cesiumPnts');
    getWebWorkerManager().init('cesiumPnts');
  }

  private async loadTileNodes(
    tileNodes: TileNode[],
    signal?: PromiseAbortSignal,
    progressController?: ProgressController,
  ) {
    if (tileNodes.length === 0) {
      return;
    }

    // Declare the nodes to be loaded as queued
    for (const tileNode of tileNodes) {
      const tileNodeUri = createTileNodeDataUri(tileNode, tileNode.content!.uri!);
      this.tileNodeLoadQueue.set(tileNodeUri, tileNode);
    }

    const deQueueNodes = () => {
      tileNodes.forEach((x) =>
        this.tileNodeLoadQueue.delete(createTileNodeDataUri(x, x.content!.uri!)),
      );
    };

    const pntsData: CesiumPntsWebworkerResponse[] = [];

    // Load a maximum of 50 nodes at a time.
    const chunks = _.chunk(tileNodes, 50);
    for (const chunk of chunks) {
      if (signal?.aborted) {
        deQueueNodes();
        return;
      }

      pntsData.push(...(await getWebWorkerManager().spawnCesiumPnts(chunk as unknown[])));

      progressController?.callback(
        pntsData.length / tileNodes.length,
        `${progressController.step} ${t('section.functions.grid.downloadingPointCloud', { ns: 'skyviewDesign' })}`,
      );
    }

    progressController?.incrementStep();

    for (let i = 0; i < pntsData.length; i++) {
      const pnts = pntsData[i];
      if (signal?.aborted) {
        deQueueNodes();
        return;
      }

      const tileNodeUri = createTileNodeDataUri(pnts.node, pnts.node.content!.uri!);
      if (!this.cache.isCached(tileNodeUri)) {
        const points = (await this.pointsBuilder(pnts.node, pnts.buffer)) as CesiumPoints;
        this.cache.addPointcloud(tileNodeUri, points);
      }

      // Clear the queue when a tile node is finished loading
      this.tileNodeLoadQueue.delete(tileNodeUri);
      progressController?.callback(
        i / pntsData.length,
        `${progressController.step} ${t('section.functions.grid.loadingPoints', { ns: 'skyviewDesign' })}`,
      );
    }

    progressController?.incrementStep();
  }

  private generateBoundingBox() {
    const sphere = extractTileNodeBoundingSphere(this.cesiumTileSetHierarchy.rootTileSet?.root!);
    this.boundingBox = sphere.getBoundingBox(new Box3());
  }

  private resolveLoadChunks() {
    // Limits traversing the hierarchy a set number of times per second
    return Date.now() - this.lastTick > 200;
  }
}
